/**
 * Public read of pricing-related system_settings (GET /api/settings/pricing-insight).
 * Admin toggles live in Admin → Settings → AI & insights → Price insight settings.
 */

import { mutate } from "swr";

/** Subset of system_settings exposed to the sell flow and insight UI. */
export type PricingInsightFeatureFlags = {
  global_pricing_insight_enabled: boolean;
  pricing_insight_enabled: boolean;
  user_pricing_insight_enabled: boolean;
  seller_price_band_enforcement_enabled: boolean;
  seller_price_band_recommendation_chips_enabled: boolean;
};

/** SWR key — must stay stable for deduping and mutate(). */
export const PRICING_INSIGHT_FLAGS_SWR_KEY = "/api/settings/pricing-insight";

/** How long to dedupe / poll (client hint; server remains authoritative). */
export const PRICING_INSIGHT_FLAGS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function pricingInsightFlagsFetcher(
  url: string,
): Promise<PricingInsightFeatureFlags> {
  const response = await fetch(url, {
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch pricing insight settings");
  }
  const data = await response.json();
  return {
    global_pricing_insight_enabled: Boolean(
      data.global_pricing_insight_enabled,
    ),
    pricing_insight_enabled: Boolean(data.pricing_insight_enabled),
    user_pricing_insight_enabled: Boolean(data.user_pricing_insight_enabled),
    seller_price_band_enforcement_enabled: Boolean(
      data.seller_price_band_enforcement_enabled,
    ),
    seller_price_band_recommendation_chips_enabled: Boolean(
      data.seller_price_band_recommendation_chips_enabled,
    ),
  };
}

/** Revalidates pricing flags for all useSWR subscribers (e.g. after admin saves settings). */
export function dispatchPricingInsightFlagsInvalidate() {
  void mutate(PRICING_INSIGHT_FLAGS_SWR_KEY);
}
